import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { thesisApi } from '../lib/api';
import { formatRelativeTime, cn } from '../lib/utils';
import {
  Plus, Lightbulb, Loader2, Sparkles, Trash2, ChevronRight,
  ChevronDown, Building2, DollarSign, Users, Zap, ExternalLink,
  TrendingUp, AlertTriangle, CheckCircle2, Clock, Search, RefreshCw,
} from 'lucide-react';

const SECTORS = [
  'AI/ML', 'Fintech', 'SaaS', 'Healthcare', 'Infrastructure',
  'Consumer', 'Climate', 'Crypto/Web3', 'Developer Tools',
  'EdTech', 'HRTech', 'Logistics', 'Real Estate', 'Legal',
  'Space', 'Robotics', 'Biotech', 'Vertical SaaS',
];

// ─── Agent badge configs ────────────────────────────────────────────────────
const AGENT_CONFIG = {
  vc: { label: 'VC Partner', icon: DollarSign, color: 'text-blue-400', bg: 'bg-blue-950/40 border-blue-800/50' },
  angel: { label: 'Angel Investor', icon: Users, color: 'text-purple-400', bg: 'bg-purple-950/40 border-purple-800/50' },
  founder: { label: 'Operator / Founder', icon: Zap, color: 'text-amber-400', bg: 'bg-amber-950/40 border-amber-800/50' },
};

const REC_CONFIG = {
  INVEST: { label: 'Invest', color: 'text-emerald-400', bg: 'bg-emerald-950/50 border-emerald-700' },
  WATCH: { label: 'Watch', color: 'text-amber-400', bg: 'bg-amber-950/50 border-amber-700' },
  PASS: { label: 'Pass', color: 'text-rose-400', bg: 'bg-rose-950/50 border-rose-700' },
};

const STRENGTH_CONFIG = {
  STRONG: { label: 'Strong fit', color: 'text-emerald-400' },
  MODERATE: { label: 'Moderate fit', color: 'text-amber-400' },
  WEAK: { label: 'Weak fit', color: 'text-zinc-500' },
};

// ─── Sub-components ─────────────────────────────────────────────────────────

function AgentCard({ agentType, analysis }) {
  const config = AGENT_CONFIG[agentType];
  const rec = REC_CONFIG[analysis?.recommendation] || REC_CONFIG.WATCH;
  const Icon = config.icon;

  if (!analysis) return null;

  return (
    <div className={`rounded-lg border p-4 ${config.bg}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${config.color}`} />
          <span className={`text-sm font-semibold font-['Outfit'] ${config.color}`}>
            {config.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded border ${rec.bg} ${rec.color} font-mono`}>
            {rec.label}
          </span>
          <span className="text-sm font-bold text-zinc-200 font-mono">
            {analysis.score}/100
          </span>
        </div>
      </div>

      <p className="text-sm text-zinc-300 font-['Manrope'] mb-3 italic leading-relaxed">
        "{analysis.verdict}"
      </p>

      {analysis.thesis_points?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1.5">Thesis Fit</p>
          <ul className="space-y-1">
            {analysis.thesis_points.slice(0, 3).map((pt, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-zinc-300">
                <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                {pt}
              </li>
            ))}
          </ul>
        </div>
      )}

      {analysis.risks?.length > 0 && (
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1.5">Key Risks</p>
          <ul className="space-y-1">
            {analysis.risks.slice(0, 2).map((risk, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-zinc-400">
                <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                {risk}
              </li>
            ))}
          </ul>
        </div>
      )}

      {analysis.questions_to_ask?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-zinc-700/50">
          <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1.5">Ask</p>
          {analysis.questions_to_ask.slice(0, 2).map((q, i) => (
            <p key={i} className="text-xs text-zinc-400 mb-1">• {q}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function ConsensusBar({ aggregate, consensus }) {
  const color = consensus === 'INVEST' ? 'bg-emerald-500' : consensus === 'PASS' ? 'bg-rose-500' : 'bg-amber-500';
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${aggregate || 0}%` }} />
      </div>
      <span className="text-xs font-mono text-zinc-400 w-10 text-right">{aggregate || 0}</span>
    </div>
  );
}

function StartupCard({ startup, detailedAnalysis, rank }) {
  const [expanded, setExpanded] = useState(false);
  const strength = STRENGTH_CONFIG[startup.strength] || STRENGTH_CONFIG.MODERATE;
  const alignPct = Math.round((startup.alignment_score || startup._score || 0.5) * 100);
  const consensus = detailedAnalysis?.consensus;
  const aggregate = detailedAnalysis?.aggregate_score;
  const recConfig = consensus ? REC_CONFIG[consensus] : null;

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
      {/* Header row */}
      <div
        className="flex items-center gap-3 p-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="text-xs font-mono text-zinc-600 w-6 text-right flex-shrink-0">
          {rank}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-medium text-zinc-50 font-['Outfit'] truncate">
              {startup.name}
            </span>
            {startup.yc_batch && (
              <span className="text-xs bg-orange-950/50 border border-orange-800/40 text-orange-400 px-1.5 py-0.5 rounded font-mono flex-shrink-0">
                YC {startup.yc_batch}
              </span>
            )}
            {startup.source_vc && !startup.yc_batch && (
              <span className="text-xs bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded font-mono flex-shrink-0 truncate max-w-[120px]">
                {startup.source_vc}
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-500 font-['Manrope'] truncate">
            {startup.description || 'No description'}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Sector tag */}
          {startup.sector && (
            <span className="text-xs text-zinc-400 hidden md:block">{startup.sector}</span>
          )}

          {/* Alignment score */}
          <div className="text-right">
            <div className="text-sm font-bold font-mono text-zinc-200">{alignPct}%</div>
            <div className={`text-xs ${strength.color}`}>{strength.label}</div>
          </div>

          {/* Consensus badge */}
          {recConfig && (
            <span className={`text-xs px-2 py-0.5 rounded border ${recConfig.bg} ${recConfig.color} font-mono hidden sm:block`}>
              {recConfig.label}
            </span>
          )}

          {/* Expand toggle */}
          {expanded
            ? <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
            : <ChevronRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-zinc-800 p-4">
          {/* Brief analysis */}
          {startup.brief_analysis && (
            <div className="mb-4 p-3 bg-zinc-800/40 rounded-lg">
              <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">Overview</p>
              <p className="text-sm text-zinc-300 font-['Manrope'] leading-relaxed">
                {startup.brief_analysis}
              </p>
            </div>
          )}

          {/* Aggregate bar */}
          {aggregate && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-xs text-zinc-500 uppercase tracking-wide">Agent Consensus</p>
                <span className={`text-xs font-semibold ${recConfig?.color || 'text-zinc-400'}`}>
                  {consensus}
                </span>
              </div>
              <ConsensusBar aggregate={aggregate} consensus={consensus} />
            </div>
          )}

          {/* 3-agent analyses */}
          {detailedAnalysis && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {Object.entries(AGENT_CONFIG).map(([type]) => (
                detailedAnalysis[type] && (
                  <AgentCard
                    key={type}
                    agentType={type}
                    analysis={detailedAnalysis[type]}
                  />
                )
              ))}
            </div>
          )}

          {!detailedAnalysis && (
            <p className="text-xs text-zinc-600 text-center py-2">
              Deep agent analysis only available for top 10 matches
            </p>
          )}

          {/* Links */}
          <div className="mt-3 flex items-center gap-3">
            {startup.website && (
              <a
                href={startup.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-zinc-400 hover:text-zinc-200 flex items-center gap-1 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-3 h-3" />
                Website
              </a>
            )}
            {startup.city && (
              <span className="text-xs text-zinc-600">{startup.city}</span>
            )}
            {startup.stage && startup.stage !== 'unknown' && (
              <span className="text-xs text-zinc-600 capitalize">{startup.stage} stage</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const ThesesPage = () => {
  const [loading, setLoading] = useState(true);
  const [theses, setTheses] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newThesis, setNewThesis] = useState({ title: '', sector: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [selectedThesis, setSelectedThesis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [sourcing, setSourcing] = useState(false);
  const [sourcingResult, setSourcingResult] = useState(null);
  const [sourcingError, setSourcingError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('sourced'); // 'sourced' | 'analysis'

  const fetchTheses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await thesisApi.list();
      setTheses(response.data.theses || []);
    } catch (err) {
      console.error('Failed to fetch theses:', err);
      setTheses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTheses();
  }, [fetchTheses]);

  // Load cached sourcing result when thesis is selected
  useEffect(() => {
    if (!selectedThesis) {
      setSourcingResult(null);
      setSourcingError(null);
      return;
    }
    setSourcingResult(null);
    setSourcingError(null);

    thesisApi.getSourced(selectedThesis.thesis_id)
      .then(res => setSourcingResult(res.data))
      .catch(() => {/* no cached result yet, that's fine */});
  }, [selectedThesis?.thesis_id]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await thesisApi.create(newThesis);
      const created = response.data;
      setTheses([created, ...theses]);
      setShowCreateModal(false);
      setNewThesis({ title: '', sector: '', description: '' });
      setSelectedThesis(created);
    } catch (err) {
      console.error('Failed to create thesis:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleAnalyze = async (thesisId) => {
    setAnalyzing(true);
    setActiveTab('analysis');
    try {
      const response = await thesisApi.analyze(thesisId);
      setTheses(theses.map(t => t.thesis_id === thesisId ? response.data : t));
      setSelectedThesis(response.data);
    } catch (err) {
      console.error('Failed to analyze thesis:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSource = async (thesisId) => {
    setSourcing(true);
    setSourcingError(null);
    setActiveTab('sourced');
    try {
      const response = await thesisApi.source(thesisId, { topN: 50, deepN: 10 });
      setSourcingResult(response.data);
    } catch (err) {
      console.error('Sourcing failed:', err);
      setSourcingError(err?.response?.data?.detail || 'Sourcing failed. Try again.');
    } finally {
      setSourcing(false);
    }
  };

  const handleDelete = async (thesisId) => {
    if (!window.confirm('Delete this thesis and all its sourcing results?')) return;
    try {
      await thesisApi.delete(thesisId);
      setTheses(theses.filter(t => t.thesis_id !== thesisId));
      if (selectedThesis?.thesis_id === thesisId) {
        setSelectedThesis(null);
        setSourcingResult(null);
      }
    } catch (err) {
      console.error('Failed to delete thesis:', err);
    }
  };

  // Filter + sort sourced startups
  const filteredStartups = (sourcingResult?.startups || []).filter(s => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (s.name || '').toLowerCase().includes(q) ||
      (s.description || '').toLowerCase().includes(q) ||
      (s.sector || '').toLowerCase().includes(q)
    );
  });

  return (
    <MainLayout>
      <div className="p-6 h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-light font-['Outfit'] text-zinc-50">Thesis Lab</h1>
            <p className="text-zinc-400 text-sm font-['Manrope']">
              Build investment theses · Source 50 matched startups · Run 3-agent AI diligence
            </p>
          </div>
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="bg-white text-zinc-900 hover:bg-zinc-200">
                <Plus className="w-4 h-4 mr-2" />
                New Thesis
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-zinc-50 font-['Outfit']">Create New Thesis</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="mt-4 space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Thesis Title</label>
                  <Input
                    value={newThesis.title}
                    onChange={(e) => setNewThesis({ ...newThesis, title: e.target.value })}
                    placeholder="e.g., AI-Native Vertical SaaS for Healthcare"
                    required
                    data-testid="input-thesis-title"
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">Primary Sector</label>
                  <Select
                    value={newThesis.sector}
                    onValueChange={(v) => setNewThesis({ ...newThesis, sector: v })}
                  >
                    <SelectTrigger data-testid="select-thesis-sector" className="bg-zinc-800 border-zinc-700 text-zinc-50">
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {SECTORS.map(s => (
                        <SelectItem key={s} value={s} className="text-zinc-50">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-zinc-400 mb-1 block">
                    Thesis Description
                    <span className="text-zinc-600 ml-2 text-xs">(be specific — AI uses this to source and score)</span>
                  </label>
                  <textarea
                    value={newThesis.description}
                    onChange={(e) => setNewThesis({ ...newThesis, description: e.target.value })}
                    placeholder="Describe your investment thesis in detail. Include target stage, geography, business model preferences, and what makes an ideal investment..."
                    rows={5}
                    data-testid="input-thesis-description"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-zinc-50 text-sm resize-none focus:outline-none focus:border-zinc-500"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="border-zinc-700 text-zinc-400">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={creating || !newThesis.title || !newThesis.sector}
                    data-testid="submit-thesis-btn"
                    className="bg-white text-zinc-900 hover:bg-zinc-200"
                  >
                    {creating ? 'Creating...' : 'Create Thesis'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Body: 2-column */}
        <div className="flex-1 flex gap-6 overflow-hidden">
          {/* ── Left: thesis list ─────────────────────────────────────────── */}
          <div className="w-72 flex-shrink-0 overflow-y-auto">
            {loading ? (
              <div className="space-y-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-md p-4 animate-pulse">
                    <div className="h-5 w-32 bg-zinc-800 rounded mb-2" />
                    <div className="h-4 w-20 bg-zinc-800 rounded" />
                  </div>
                ))}
              </div>
            ) : theses.length > 0 ? (
              <div className="space-y-2">
                {theses.map(thesis => (
                  <div
                    key={thesis.thesis_id}
                    onClick={() => setSelectedThesis(thesis)}
                    data-testid={`thesis-card-${thesis.thesis_id}`}
                    className={cn(
                      "bg-zinc-900 border rounded-md p-4 cursor-pointer transition-colors group",
                      selectedThesis?.thesis_id === thesis.thesis_id
                        ? "border-zinc-600"
                        : "border-zinc-800 hover:border-zinc-700"
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-zinc-50 font-medium font-['Outfit'] text-sm truncate">{thesis.title}</h3>
                        <p className="text-zinc-400 text-xs font-['Manrope'] mt-0.5">{thesis.sector}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 flex-shrink-0 mt-0.5" />
                    </div>
                    {thesis.matching_startup_ids?.length > 0 && (
                      <div className="mt-2 flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-zinc-600" />
                        <span className="text-xs text-zinc-600 font-mono">
                          {thesis.matching_startup_ids.length} sourced
                        </span>
                      </div>
                    )}
                    <p className="text-zinc-600 text-xs font-mono mt-1">
                      {formatRelativeTime(thesis.updated_at)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-md p-8 text-center">
                <Lightbulb className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400 text-sm font-['Manrope'] mb-4">No theses yet</p>
                <Button onClick={() => setShowCreateModal(true)} className="bg-white text-zinc-900 hover:bg-zinc-200">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Thesis
                </Button>
              </div>
            )}
          </div>

          {/* ── Right: thesis detail ──────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto">
            {selectedThesis ? (
              <div className="space-y-4">
                {/* Thesis header card */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0 mr-4">
                      <h2 className="text-xl font-medium font-['Outfit'] text-zinc-50 mb-1">
                        {selectedThesis.title}
                      </h2>
                      <p className="text-zinc-400 text-sm">{selectedThesis.sector}</p>
                      {selectedThesis.description && (
                        <p className="text-zinc-400 text-sm font-['Manrope'] mt-2 leading-relaxed">
                          {selectedThesis.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        onClick={() => handleSource(selectedThesis.thesis_id)}
                        disabled={sourcing || analyzing}
                        data-testid="source-thesis-btn"
                        className="bg-white text-zinc-900 hover:bg-zinc-200"
                      >
                        {sourcing
                          ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sourcing...</>
                          : sourcingResult
                          ? <><RefreshCw className="w-4 h-4 mr-2" />Re-Source</>
                          : <><Sparkles className="w-4 h-4 mr-2" />Source 50 Startups</>}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleAnalyze(selectedThesis.thesis_id)}
                        disabled={sourcing || analyzing}
                        data-testid="analyze-thesis-btn"
                        className="border-zinc-700 text-zinc-400 hover:text-zinc-50"
                      >
                        {analyzing
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <TrendingUp className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => handleDelete(selectedThesis.thesis_id)}
                        className="text-zinc-600 hover:text-rose-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Sourcing status bar */}
                  {sourcing && (
                    <div className="flex items-center gap-3 p-3 bg-zinc-800/40 rounded-lg">
                      <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
                      <div>
                        <p className="text-sm text-zinc-300">Sourcing startups from database...</p>
                        <p className="text-xs text-zinc-500">Running VC · Angel · Founder agent analyses on top 10 matches</p>
                      </div>
                    </div>
                  )}

                  {sourcingError && (
                    <div className="p-3 bg-rose-950/30 border border-rose-800/40 rounded-lg text-sm text-rose-400">
                      {sourcingError}
                    </div>
                  )}
                </div>

                {/* Tabs */}
                {(sourcingResult || selectedThesis.whitespace_analysis) && (
                  <div className="flex gap-1 bg-zinc-900 border border-zinc-800 rounded-lg p-1">
                    <button
                      onClick={() => setActiveTab('sourced')}
                      className={cn(
                        "flex-1 py-2 px-4 rounded-md text-sm font-['Outfit'] transition-colors",
                        activeTab === 'sourced'
                          ? "bg-zinc-800 text-zinc-50"
                          : "text-zinc-500 hover:text-zinc-300"
                      )}
                    >
                      Sourced Startups
                      {sourcingResult?.startups?.length > 0 && (
                        <span className="ml-2 text-xs text-zinc-500 font-mono">
                          {sourcingResult.startups.length}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('analysis')}
                      className={cn(
                        "flex-1 py-2 px-4 rounded-md text-sm font-['Outfit'] transition-colors",
                        activeTab === 'analysis'
                          ? "bg-zinc-800 text-zinc-50"
                          : "text-zinc-500 hover:text-zinc-300"
                      )}
                    >
                      Market Analysis
                    </button>
                  </div>
                )}

                {/* ── Sourced startups tab ───────────────────────────────── */}
                {activeTab === 'sourced' && sourcingResult && (
                  <div>
                    {/* Meta bar */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-zinc-400">
                          <span className="font-mono text-zinc-200">{sourcingResult.startups?.length || 0}</span>
                          {' '}startups sourced from{' '}
                          <span className="font-mono text-zinc-200">{sourcingResult.total_matched?.toLocaleString() || '?'}</span>
                          {' '}matches
                        </span>
                        <span className="text-xs text-zinc-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {sourcingResult.sourced_at
                            ? new Date(sourcingResult.sourced_at).toLocaleDateString()
                            : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-zinc-500" />
                        <input
                          type="text"
                          placeholder="Filter startups..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="bg-zinc-800 border border-zinc-700 rounded-md px-3 py-1.5 text-sm text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-zinc-500 w-48"
                        />
                      </div>
                    </div>

                    {/* Agent key */}
                    <div className="flex items-center gap-4 mb-3 px-1">
                      <span className="text-xs text-zinc-600">Deep analysis on top 10:</span>
                      {Object.entries(AGENT_CONFIG).map(([type, cfg]) => {
                        const Icon = cfg.icon;
                        return (
                          <span key={type} className={`text-xs flex items-center gap-1 ${cfg.color}`}>
                            <Icon className="w-3 h-3" />
                            {cfg.label}
                          </span>
                        );
                      })}
                    </div>

                    {/* Startup list */}
                    <div className="space-y-2">
                      {filteredStartups.length === 0 ? (
                        <div className="text-center py-8 text-zinc-600 text-sm">
                          No startups match your filter
                        </div>
                      ) : (
                        filteredStartups.map((s, i) => (
                          <StartupCard
                            key={s.startup_id || i}
                            startup={s}
                            rank={i + 1}
                            detailedAnalysis={sourcingResult.detailed_analyses?.[s.startup_id]}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* ── No sourcing yet prompt ─────────────────────────────── */}
                {activeTab === 'sourced' && !sourcingResult && !sourcing && (
                  <div className="bg-zinc-900 border border-zinc-800 border-dashed rounded-lg p-12 text-center">
                    <Sparkles className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
                    <p className="text-zinc-400 font-['Outfit'] mb-2">No sourcing run yet</p>
                    <p className="text-zinc-600 text-sm font-['Manrope'] mb-6">
                      Click "Source 50 Startups" to find the best matches from the database
                      and run VC · Angel · Founder diligence on top candidates.
                    </p>
                    <Button
                      onClick={() => handleSource(selectedThesis.thesis_id)}
                      className="bg-white text-zinc-900 hover:bg-zinc-200"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Source 50 Startups
                    </Button>
                  </div>
                )}

                {/* ── Market analysis tab ────────────────────────────────── */}
                {activeTab === 'analysis' && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-5">
                    {selectedThesis.whitespace_analysis ? (
                      <>
                        <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wide mb-3">
                          Market Analysis
                        </h3>
                        <div className="bg-zinc-800/40 rounded-lg p-4">
                          <pre className="text-zinc-300 text-sm whitespace-pre-wrap font-['Manrope'] leading-relaxed">
                            {selectedThesis.whitespace_analysis}
                          </pre>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <TrendingUp className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
                        <p className="text-zinc-500 text-sm mb-4">No analysis generated yet</p>
                        <Button
                          onClick={() => handleAnalyze(selectedThesis.thesis_id)}
                          disabled={analyzing}
                          className="bg-white text-zinc-900 hover:bg-zinc-200"
                        >
                          {analyzing
                            ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Analyzing...</>
                            : <><TrendingUp className="w-4 h-4 mr-2" />Generate Analysis</>}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <Lightbulb className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500 font-['Manrope'] mb-2">Select a thesis to view</p>
                  <p className="text-zinc-600 text-sm">or create a new one to start sourcing</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ThesesPage;
